import { useQuery } from '@apollo/client';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useLayoutEffect } from 'react';
import { BarChart } from 'react-native-gifted-charts';

import { PracticeOutcome } from '@oui/app-core/src/components/PracticeOutcome/PracticeOutcome';
import { RoundedSection } from '@oui/app-core/src/components/RoundedSection';
import { Heading, Label } from '@oui/app-core/src/components/Text';
import { View } from '@oui/app-core/src/components/View';
import { useI18n } from '@oui/app-core/src/lib/i18n';
import { useTheme } from '@oui/app-core/src/styles';
import { parseGQLDate } from '@oui/lib/src/gqlDate';
import { graphql } from '@oui/lib/src/graphql/tada';
import { times } from '@oui/lib/src/times';

import { StackScreenProps } from '../../types/navigation';

export const WordPairingShowQuery = graphql(`
  query WordPairingShow($practiceID: UUID!) {
    user {
      ID
      role {
        ID
        wordPairingWidget {
          ID
          wordPairingByID(practiceID: $practiceID) {
            practiceID
            practiceValues {
              date
              ratings {
                type
                value
              }
            }
            wordPairing {
              correctAnswersIncreased
              isComplete
              numRounds
              timeToCompleteDecreased
              rounds {
                isComplete
                numAnswers
                numCorrectAnswers
                numQuestions
                time
              }
            }
          }
        }
      }
    }
  }
`);

/**
 * Displays data for an individual word pairing practice.
 */
export const WordPairingShow = () => {
  const { setOptions } = useNavigation<StackScreenProps<'WordPairingShow'>['navigation']>();
  const route = useRoute<StackScreenProps<'WordPairingShow'>['route']>();
  const { theme } = useTheme();
  const { formatDate } = useI18n();
  const { data } = useQuery(WordPairingShowQuery, {
    variables: {
      practiceID: route.params.practiceID,
    },
  });

  const entry = data?.user?.role?.wordPairingWidget?.wordPairingByID;
  const ratingOkOrBetter = !!entry?.practiceValues.ratings.find(
    (r) => r.type === 'RATING_AFTER' && r.value >= 0,
  );

  useLayoutEffect(() => {
    setOptions({
      headerTintColor: 'white',
      headerBackTitleVisible: false,
      headerShadowVisible: false,
      headerStyle: {
        backgroundColor: theme.color.dark,
      },
      cardStyle: { backgroundColor: theme.color.dark },
    });
  }, [setOptions, theme]);

  return (
    <RoundedSection
      testID="WordPairingShow"
      color={theme.color.dark}
      title={
        entry
          ? formatDate(parseGQLDate(entry.practiceValues.date), {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
            })
          : ''
      }
      secondaryColor="white"
      applyHeaderOptions
    >
      {entry ? (
        <View style={{ gap: 20, flexGrow: 1 }}>
          <Heading level={3} text="Outcome" textAlign="center" />
          <PracticeOutcome
            outcomes={[
              {
                icon: 'check',
                text: 'Accuracy increased',
                value: entry.wordPairing.correctAnswersIncreased,
              },
              {
                icon: 'clock',
                text: 'Time to complete decreased',
                value: entry.wordPairing.timeToCompleteDecreased,
              },
              { icon: 'thumb-up', text: 'Rated ‘Went ok’ or better', value: ratingOkOrBetter },
            ]}
          />
          <View
            style={{
              backgroundColor: 'white',
              borderTopStartRadius: 30,
              borderTopEndRadius: 30,
              marginHorizontal: -20,
              padding: 20,
              flexGrow: 1,
              gap: 25,
            }}
          >
            <View>
              <Label small text="Accuracy (%)" />
              <BarChart
                barWidth={90}
                data={times(entry.wordPairing.numRounds, (i) => ({
                  value: entry.wordPairing.rounds[i]
                    ? (entry.wordPairing.rounds[i].numCorrectAnswers /
                        entry.wordPairing.rounds[i].numAnswers) *
                      100
                    : 0,
                  frontColor: theme.color.accent100,
                  barBorderTopLeftRadius: 3,
                  barBorderTopRightRadius: 3,
                }))}
                disablePress
                disableScroll
                endSpacing={8 / 2} // spacing seems to apply twice, so divide by 2
                initialSpacing={8 / 2} // spacing seems to apply twice, so divide by 2
                maxValue={100}
                spacing={14}
                xAxisLabelTextStyle={{ fontFamily: 'OpenSansRegular', ...theme.typography.small }}
                xAxisLabelTexts={times(entry.wordPairing.numRounds, (i) => (i + 1).toString())}
                yAxisTextStyle={{ fontFamily: 'OpenSansRegular', ...theme.typography.small }}
              />
              <Label small text="Rounds" textAlign="center" />
            </View>
            <View
              style={{
                alignSelf: 'stretch',
                marginHorizontal: -20,
                height: 1,
                backgroundColor: theme.color.dark,
              }}
            />
            <View>
              <Label small text="Avg seconds to answer" />
              <BarChart
                barWidth={90}
                data={times(entry.wordPairing.numRounds, (i) => ({
                  value: entry.wordPairing.rounds[i]
                    ? entry.wordPairing.rounds[i].time /
                      entry.wordPairing.rounds[i].numAnswers /
                      1000
                    : 0,
                  frontColor: theme.color.gray500,
                  barBorderTopLeftRadius: 3,
                  barBorderTopRightRadius: 3,
                }))}
                disablePress
                disableScroll
                endSpacing={8 / 2} // spacing seems to apply twice, so divide by 2
                initialSpacing={8 / 2} // spacing seems to apply twice, so divide by 2
                spacing={14}
                xAxisLabelTextStyle={{ fontFamily: 'OpenSansRegular', ...theme.typography.small }}
                xAxisLabelTexts={times(entry.wordPairing.numRounds, (i) => (i + 1).toString())}
                showFractionalValues
                yAxisTextStyle={{ fontFamily: 'OpenSansRegular', ...theme.typography.small }}
              />
              <Label small text="Rounds" textAlign="center" />
            </View>
          </View>
        </View>
      ) : null}
    </RoundedSection>
  );
};
